/** Corta o fluxo de bytes do canal "transfer" pelos tamanhos da oferta. */

export interface FileSlice {
  index: number;
  bytes: Uint8Array;
}

export class FileCutter {
  private index = 0;
  private filled = 0;

  constructor(private readonly sizes: readonly number[]) {}

  get done(): boolean {
    return this.index >= this.sizes.length;
  }

  /**
   * Os pedaços apontam para `data` (não copie se for guardar além deste push).
   * `extra` é o que passou da soma dos tamanhos.
   */
  push(data: Uint8Array): { slices: FileSlice[]; done: boolean; extra: Uint8Array } {
    const slices: FileSlice[] = [];
    let offset = 0;
    while (this.index < this.sizes.length && (offset < data.length || this.sizes[this.index] === 0)) {
      const size = this.sizes[this.index] ?? 0;
      if (size === 0) {
        slices.push({ index: this.index, bytes: new Uint8Array(0) });
        this.index += 1;
        this.filled = 0;
        continue;
      }
      if (offset >= data.length) break;
      const take = Math.min(size - this.filled, data.length - offset);
      slices.push({ index: this.index, bytes: data.subarray(offset, offset + take) });
      offset += take;
      this.filled += take;
      if (this.filled === size) {
        this.index += 1;
        this.filled = 0;
      }
    }
    const extra = this.done ? data.subarray(offset) : data.subarray(0, 0);
    return { slices, done: this.done, extra };
  }
}

/** Junta um buffer já completo. O que sobra não pertence a estes arquivos. */
export function splitBySizes(data: Uint8Array, sizes: number[]): { files: Uint8Array[]; rest: Uint8Array } {
  const cutter = new FileCutter(sizes);
  const parts: Uint8Array[][] = sizes.map(() => []);
  const { slices, extra } = cutter.push(data);
  for (const slice of slices) parts[slice.index]?.push(slice.bytes.slice());
  return { files: parts.map(concatBytes), rest: extra.slice() };
}

export function concatBytes(parts: Uint8Array[]): Uint8Array {
  const total = parts.reduce((sum, part) => sum + part.length, 0);
  const out = new Uint8Array(total);
  let offset = 0;
  for (const part of parts) {
    out.set(part, offset);
    offset += part.length;
  }
  return out;
}

export interface ChunkSend {
  chunkSize: number;
  highWater: number;
  bufferedAmount(): number;
  waitUntilLow(): Promise<void>;
  send(chunk: Uint8Array): void;
  isAborted?(): boolean;
}

/** Manda `bytes` em pedaços, esperando quando o canal passa de `highWater`. Devolve quantos bytes saíram. */
export async function sendInChunks(bytes: Uint8Array, options: ChunkSend): Promise<number> {
  let offset = 0;
  try {
    while (offset < bytes.length) {
      if (options.isAborted?.()) return offset;
      if (options.bufferedAmount() > options.highWater) await options.waitUntilLow();
      if (options.isAborted?.()) return offset;
      const end = Math.min(offset + options.chunkSize, bytes.length);
      options.send(bytes.subarray(offset, end).slice());
      offset = end;
    }
  } catch (error) {
    if (options.isAborted?.()) return offset;
    throw error;
  }
  return offset;
}
